import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getFormattedNumber } from '@vestigas/utils';
import { formatFileType, formatFileTypesLabel } from '~/utils';

/**
 * Every string the import wizard and the file, mapping, preview and
 * instruction components render, flat and already translated. Keeping it flat
 * and free of any config import lets a definition override a single string
 * without reaching into component internals, and keeps this module importable
 * from both the authoring types and the components.
 *
 * Entries that interpolate or pluralize are functions: their values are only
 * known where the string is rendered, and i18next needs them at call time.
 */
export type DataImportCopy = {
  /** Lists the accepted formats and pluralizes on how many there are. */
  acceptedFileTypes: (fileTypes: readonly string[]) => string;
  /** Error-boundary fallback covering a `renderAdditionalForm` that throws while rendering. */
  additionalFormFallback: string;
  additionalInformation: string;
  assignOptionalColumn: string;
  assignRequiredColumn: string;
  back: string;
  cancel: string;
  close: string;
  confirmAction: string;
  /** Reported when a CSV cannot be read at all, before any row is inspected. */
  csvParseFailed: string;
  downloadTemplate: string;
  /** Reported when a file carries no header row to map columns from. */
  emptyFile: string;
  /** Names the size bound the rejected file exceeded, already formatted. */
  fileTooLarge: (limit: string) => string;
  importing: string;
  inconsistentFieldCount: string;
  /** Names the formats that would have been accepted instead. */
  invalidFileType: (fileTypes: readonly string[]) => string;
  mapColumnsAction: string;
  /** Reported when the parsed file has more rows than the import allows. */
  maxRowsExceeded: (rowCount: number, maxRows: number) => string;
  optionalColumnsHeading: string;
  parsingFile: string;
  /** Error-boundary fallback covering the importability or confirmed-import preview. */
  previewFallback: string;
  previewFilterLabel: string;
  /** Accessible name of the control that drops a selected file. */
  removeFile: (fileName: string) => string;
  requiredColumnsHeading: string;
  requiredFieldMessage: string;
  /** Error-boundary fallback covering a `renderResult` that throws while rendering. */
  resultFallback: string;
  selectAnotherFile: string;
  selectFile: string;
  showAllRows: string;
  showErrorRows: string;
  sourceColumnsHeading: string;
  sourceLineColumn: string;
  stepMapColumns: string;
  stepPreview: string;
  stepSelectFile: string;
  /** Error-boundary fallback covering a `renderSubmitError` that throws while rendering. */
  submitErrorFallback: string;
  systemColumnsHeading: string;
  /** Pluralizes on the number of unmapped columns, so it stays a function. */
  unmappedColumnsDetails: (count: number) => string;
  unmappedColumnsWarning: string;
  /** Names the delimiters the file could have used instead. */
  unsupportedDelimiter: (delimiters: string) => string;
  validateAction: string;
  validatingPreview: string;
  /**
   * Error-boundary fallback for a failure the narrower slots above don't
   * cover — the dialog-level backstop a host wraps the whole wizard or
   * legacy import in.
   */
  wizardFallback: string;
};

/** Replaces single pieces of the built-in copy, keyed by the string it overrides. */
export type DataImportCopyOverrides = Partial<DataImportCopy>;

/**
 * An override set with explicit `undefined` values must not blank a label, so
 * only the keys that carry a value take part in the merge.
 */
function mergeCopy(
  defaults: DataImportCopy,
  overrides?: DataImportCopyOverrides,
): DataImportCopy {
  if (!overrides) return defaults;

  return Object.entries(overrides).reduce<DataImportCopy>(
    (copy, [key, value]) =>
      value === undefined ? copy : { ...copy, [key]: value },
    defaults,
  );
}

/**
 * The wizard resolves the copy once and threads it down; the components that
 * are also used standalone resolve their own, so a bare `<ColumnMappingForm>`
 * or `<DataImportPreviewTable>` still reads the built-in strings.
 */
export function useDataImportCopy(
  overrides?: DataImportCopyOverrides,
): DataImportCopy {
  const { i18n, t } = useTranslation('ui');
  const fileTypesFormatter = useMemo(
    () => new Intl.ListFormat(i18n.language, { type: 'disjunction' }),
    [i18n.language],
  );
  const numberLocale = i18n.language === 'de' ? 'de-DE' : 'en-US';

  return useMemo(
    () =>
      mergeCopy(
        {
          acceptedFileTypes: (fileTypes) =>
            t(($) => $.dataImport.acceptedFileTypes, {
              count: fileTypes.length,
              types: fileTypes
                .map((fileType) => formatFileType(fileType, t))
                .join(', '),
            }),
          additionalFormFallback: t(
            ($) => $.dataImport.wizard.errors.additionalForm,
          ),
          additionalInformation: t(($) => $.dataImport.additionalInfo),
          assignOptionalColumn: t(($) => $.dataImport.assignOptionalColumn),
          assignRequiredColumn: t(($) => $.dataImport.assignColumn),
          back: t(($) => $.dataImport.wizard.back),
          cancel: t(($) => $.dataImport.wizard.cancel),
          close: t(($) => $.dataImport.wizard.close),
          confirmAction: t(($) => $.dataImport.wizard.confirm),
          csvParseFailed: t(($) => $.dataImport.csvParseFailed),
          downloadTemplate: t(($) => $.dataImport.downloadTemplate),
          emptyFile: t(($) => $.dataImport.emptyFile),
          fileTooLarge: (limit) =>
            t(($) => $.dataImport.fileTooLarge, { limit }),
          importing: t(($) => $.dataImport.wizard.importing),
          inconsistentFieldCount: t(($) => $.dataImport.inconsistentFieldCount),
          invalidFileType: (fileTypes) =>
            t(($) => $.dataImport.invalidFileType, {
              types: formatFileTypesLabel(fileTypes, t, fileTypesFormatter),
            }),
          mapColumnsAction: t(($) => $.dataImport.wizard.mapColumns),
          maxRowsExceeded: (rowCount, maxRows) =>
            t(($) => $.dataImport.maxRowsExceeded, {
              maxRowsFormatted: getFormattedNumber<string>({
                digits: 0,
                locale: numberLocale,
                value: maxRows,
              }),
              rowCountFormatted: getFormattedNumber<string>({
                digits: 0,
                locale: numberLocale,
                value: rowCount,
              }),
            }),
          optionalColumnsHeading: t(($) => $.dataImport.columnOptional),
          parsingFile: t(($) => $.dataImport.wizard.parsingFile),
          previewFallback: t(($) => $.dataImport.wizard.errors.preview),
          previewFilterLabel: t(($) => $.dataImport.wizard.previewFilterLabel),
          removeFile: (fileName) =>
            t(($) => $.dataImport.removeFile, { fileName }),
          requiredColumnsHeading: t(($) => $.dataImport.columnMandatory),
          requiredFieldMessage: t(($) => $.dataImport.fieldMandatory),
          resultFallback: t(($) => $.dataImport.wizard.errors.result),
          selectAnotherFile: t(($) => $.dataImport.selectAnotherFile),
          selectFile: t(($) => $.dataImport.selectFile),
          showAllRows: t(($) => $.dataImport.wizard.showAllRows),
          showErrorRows: t(($) => $.dataImport.wizard.showErrorRows),
          sourceColumnsHeading: t(($) => $.dataImport.mappedColumnNames),
          sourceLineColumn: t(($) => $.dataImport.wizard.sourceLine),
          stepMapColumns: t(($) => $.dataImport.wizard.steps.mapColumns),
          stepPreview: t(($) => $.dataImport.wizard.steps.preview),
          stepSelectFile: t(($) => $.dataImport.wizard.steps.selectFile),
          submitErrorFallback: t(($) => $.dataImport.wizard.errors.submitError),
          systemColumnsHeading: t(($) => $.dataImport.systemColumnNames),
          unmappedColumnsDetails: (count) =>
            t(($) => $.dataImport.unmappedColumnsWarning.showDetails, {
              count,
            }),
          unmappedColumnsWarning: t(
            ($) => $.dataImport.unmappedColumnsWarning.message,
          ),
          unsupportedDelimiter: (delimiters) =>
            t(($) => $.dataImport.unsupportedDelimiter, { delimiters }),
          validateAction: t(($) => $.dataImport.wizard.checkImportability),
          validatingPreview: t(($) => $.dataImport.wizard.validatingPreview),
          wizardFallback: t(($) => $.dataImport.wizard.errors.generic),
        },
        overrides,
      ),
    [fileTypesFormatter, numberLocale, overrides, t],
  );
}
